import React from "react";
import { format } from "date-fns";
import _ from "lodash";

import AttributeGroupFields from "components/form-inputs/attribute-group-fields";
import CheckboxPicklistDisplay from "components/form-inputs/checkbox-picklist/read-only";
import ColorPickerDisplay from "components/form-inputs/color-picker/display";
import ImagePickerDisplay from "components/form-inputs/image-picker/display";
import NutritionReadOnly from "components/attributes/panels/nutritionpanel/read-only";
import ReadOnlyDrugPanel from "components/attributes/panels/drug/components/read-only";
import RichTextEditor from "components/form-inputs/rich-text";
import ThreeWayCheckbox from "components/form-inputs/boolean";
import ModalButton from "components/attributes/compare-table/modal-button";
import SectionLaunch from "../section-launch";

import { DATE_FNS_LONG_DATE_FORMAT } from "shared/constants";
import { get, patch, post, deleteRequest, useMock, put } from "shared/data";
import { isBoolean, objectToParamString } from "shared/helpers";
import { displayMeasurements } from "components/form-inputs/peg-measurements/lib/data";
import { sortAttributes } from "components/attributes/lib/helpers";
import { ReadOnlyLightingPanel } from "components/attributes/panels/lighting/components/read-only";
import { ReadOnlyEnergyPanel } from "components/attributes/panels/energy/components/read-only";
import { ReadOnlyGuaranteedAnalysisPanel } from "../panels/guaranteed-analysis/components/read-only";
import { DraggablePanelButton } from "../panels/components/draggable-panel/draggable-panel-button";
import { coderRoleLabel, coderRoleType } from "./constants";

const attributeCache = {};

export const getWip = (key, includeInvisible) => {
    const hiddenParam = includeInvisible ? "?includeInvisible=true" : "";
    const url = `/coding/wips/${key}${hiddenParam}`;

    return get(url, { defaultReturn: { attributes: {}, product: {} } }).then((ref) => {
        const attributes = ref;
        const product = {
            brand: ref.brand,
            key: ref.key,
            modifier: ref.modifier,
            upc: ref.upc || ref.Upc,
        };
        attributeCache[key] = attributes;

        return {
            product,
            attributes,
        };
    });
};

export const getWips = (keys, includeInvisible) => {
    const payload = {
        codingWipKeys: keys,
        includeInvisible,
    };

    return post("/coding/wips/attribute-search", payload, { defaultReturn: {} });
};

export const getAttributeDataByWipWithValues = (codingWip) => {
    const payload = {
        wip: codingWip,
        pids: [],
    };

    const scaffoldPromise = getAttributesForReferences(payload);
    const valuesPromise = getWip(codingWip);

    return Promise.all([scaffoldPromise, valuesPromise]).then((values) => {
        const scaffold = values[0];
        const attributeValues = values[1];

        return scaffold.map((s) => {
            return {
                ...s,
                value: attributeValues.attributes[s.uuid],
            };
        });
    });
};

export const mapAttribute = (attribute) => {
    return {
        ...attribute,
        validation: {
            regEx: attribute.regEx,
            regExFailureMessage: attribute.regExFailureMessage,
            type: attribute.componentType,
            max: attribute.maxChars > 0 ? attribute.maxChars : null,
            min: attribute.minChars > 0 ? attribute.minChars : null,
            textTransform: attribute.textTransform,
            required: attribute.required || false,
        },
    };
};

export const mapAttributeData = (data) => {
    if (!data) {
        return [];
    }

    return data.reduce((arr, attribute) => {
        arr.push(mapAttribute(attribute));

        return arr;
    }, []);
};

const mapSubgroupsAttributes = (data) => {
    const remapSubgroups = (values) => {
        return values.map((subGroup) => {
            if (subGroup.attributes.length) {
                const attrs = mapAttributeData(subGroup.attributes);

                return {
                    ...subGroup,
                    attributes: attrs,
                };
            }

            if (subGroup.subGroups.length) {
                return {
                    ...subGroup,
                    subGroups: remapSubgroups(subGroup.subGroups),
                };
            }

            return subGroup;
        }, []);
    };

    return {
        ...data,
        subGroups: remapSubgroups(data.subGroups),
    };
};

export const mapAttributesToAttributeRow = (attributeList = [], referenceProducts, currentProduct) => {
    return attributeList.reduce((bodyData, attribute) => {
        const referenceValues = referenceProducts.map((reference) => {
            if (reference.isIntake) {
                return reference[attribute.uuid];
            } else {
                return reference[attribute.key.toLowerCase()];
            }
        });

        bodyData[attribute.uuid] = [
            {
                ...attribute,
                dataType: attribute.componentType,
            },
            {
                ...attribute,
                dataType: attribute.componentType,
                value: currentProduct[attribute.uuid], // < -- USER VALUE HERE
                additionalInfo: attribute.additionalInfo || null,
            },
            ...referenceValues.map((rv) => {
                return {
                    dataType: attribute.componentType,
                    value: rv,
                    required: attribute.required,
                    readOnly: attribute.readOnly,
                    additionalInfo: attribute.additionalInfo || null,
                };
            }),
        ];

        return bodyData;
    }, {});
};

export const getAttributesForReferences = (payload) => {
    // **** Expected To Return:
    // [
    //     {
    //         "key": "string",
    //         "name": "string",
    //         "shortDescription": "string",
    //         "dataType": "String",
    //         "tooltip": "string",
    //         "examples": "string",
    //         "limitTo": [
    //         {
    //             "text": "string",
    //             "value": "string"
    //         }
    //         ]
    //     }
    // ]
    const paramString = payload.pids.reduce((memo, key) => {
        return memo + `&pid=${key}`;
    }, "");

    const taskType = payload.taskType ? `&taskType=${payload.taskType}` : "";

    const url = useMock ? "/coding/attribute-scaffold.json" : `/coding/attributes/reference?codingWipKey=${payload.wip}${paramString}${taskType}`;

    return get(url, { useMock }).then(mapAttributeData);
    /*.then(attributes => {
            
            return attributes.reduce((memo, attribute) => {
                if(!attribute) {
                    return memo;
                }
                memo.push(attribute);
                return memo;
            }, []);
        });*/
};

export const setSourcePage = (sourcePage, isFirstParam) => {
    const firstParam = isFirstParam ? "?" : "&";

    return sourcePage ? `${firstParam}sourcePage=${sourcePage}` : "";
};

export const searchAttributes = (value, sourcePage) => {
    return get(`/coding/attributes?q=${value}${setSourcePage(sourcePage, false)}`).then(mapAttributeData);
};

export const updateAttribute = (payload) => {
    return put("/coding/attributes", payload);
};

export const mapAttributesToReadOnlyRow = (attributeList = [], referenceProducts) => {
    return attributeList.reduce((bodyData, attribute) => {
        const referenceValues = referenceProducts.map((reference) => {
            if (reference.isIntake) {
                return reference[attribute.uuid];
            } else {
                return reference[attribute.key.toLowerCase()];
            }
        });

        bodyData[attribute.uuid] = [
            {
                dataType: attribute.componentType,
                name: attribute.name,
                required: attribute.required,
                readOnly: attribute.readOnly,
            },
            ...referenceValues.map((rv) => {
                return {
                    dataType: attribute.componentType,
                    value: rv,
                    required: attribute.required,
                    readOnly: attribute.readOnly,
                };
            }),
        ];

        return bodyData;
    }, {});
};

export const getDefaultValueByType = (type = "") => {
    switch (type.toLowerCase()) {
        case "string":
        case "number":
        case "boolean":
            return "";
        case "decimal":
            return "";
        case "stringlist":
        case "LinkList":
        case "numberlist":
        case "decimallist":
        case "measurementlist":
        case "colorpicklist":
        case "multidropdownpicklist":
        case "multidropdowncheckboxpicklist":
        case "pegmeasurement":
        case "featurebullet":
            return [];
        case "drugpanel":
        case "drug_panel":
        case "energypanel":
        case "energy_panel":
        case "lightingpanel":
        case "lighting_panel":
        case "nutritionpanel":
        case "nutrition_panel":
        case "supplementpanel":
        case "supplement_panel":
        case "attributegroup":
        case "varietypack":
            return {};
        default:
            return "";
    }
};

export const parseDefaultValue = (value, type) => {
    let valueObject, defaultValue;
    try {
        valueObject = JSON.parse(value);
    } catch (e) {
        return "";
    }

    switch (type) {
        case "Boolean":
            defaultValue = valueObject.value === false || valueObject.value === true ? valueObject.value.toString() : "";
            break;
        case "Measurement":
            defaultValue = valueObject.value && valueObject.uom ? valueObject : "";
            break;
        case "ImagePicklist":
        case "MeasurementList":
        case "StringList":
        case "LinkList":
        case "NumberList":
        case "Picklist":
        case "CascadingPicklist":
        case "EnergyPanel":
        case "LightingPanel":
        case "NutritionPanel":
        case "SupplementPanel":
        case "DrugPanel":
        default:
            defaultValue = valueObject.value ? valueObject.value : "";
            break;
    }

    return defaultValue;
};

// TODO: find a better way of passing "upc" for references
export const readOnlyValue = (value, type, attrData, upc, wipKey, taskType, roleType, roleLabel) => {
    let textValue = value;

    switch (type) {
        case "String":
        case "Number":
        case "Decimal":
            textValue = typeof value === "string" || typeof value === "number" ? String(value) : "";
            const attributeDates = (attrData && attrData.key === "CreatedOn") || (attrData && attrData.key === "PostDate");

            if (attributeDates && !!textValue && textValue !== "N/A") {
                textValue = format(new Date(textValue), DATE_FNS_LONG_DATE_FORMAT);
            }

            if (typeof value === "string" && value.match(/\r?\n/)) {
                textValue = (
                    <>
                        {value.split(/\r?\n/).map((line, index) => (
                            <div key={index}>{line}</div>
                        ))}
                    </>
                );
            }

            break;
        case "Boolean":
            textValue = value === true || value === "true" ? "Yes" : "No";
            break;
        case "BooleanCheckbox":
            textValue = (
                <ThreeWayCheckbox
                    defaultValue={value}
                    readOnly={true}
                />
            );
            break;
        case "MultiDropdownCheckboxPicklist":
            textValue = (
                <CheckboxPicklistDisplay
                    selections={value}
                    readOnly={true}
                />
            );
            break;
        case "Measurement":
            const uomText = value && value.hasOwnProperty("uom") && value.uom && value.uom.hasOwnProperty("text") ? value.uom.text : "";
            textValue = value && value.hasOwnProperty("value") ? `${value.value} ${uomText}` : "";
            break;
        case "MeasurementList":
            textValue =
                value && value.length > 0
                    ? value
                          .map((m) => {
                              return `${m.value} ${m.uom}`;
                          })
                          .join(", ")
                    : "";
            break;
        case "Date":
            textValue = moment.unix(value).format(MOMENT_SHORT_DATE_FORMAT);
            break;
        case "DropdownPickList":
            if (value && value.hasOwnProperty("value")) {
                textValue = value.text;
            } else if (value && value.hasOwnProperty("text")) {
                textValue = value.text;
            } else {
                textValue = "";
            }

            break;
        case "MultiDropdownPicklist":
        case "CascadingPicklist":
        case "Picklist":
        case "StringList":
        case "FeatureBullet":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else if (typeof value === "string") {
                textValue = value;
            } else if (Array.isArray(value)) {
                const textValues = _.compact(value).map((v) => {
                    return typeof v === "string" ? v : v.text;
                });
                const list = textValues.map((v, i) => <li key={`feature-bullet_${i}`}>{v}</li>);
                textValue = <ul>{list}</ul>;
            } else if (value && value.hasOwnProperty("text")) {
                textValue = value.text || "";
            } else {
                textValue = <div className="txt-error">Error: This value does not conform to standards</div>;
            }

            break;
        case "LinkList":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else if (typeof value === "string") {
                textValue = (
                    <a
                        href={value}
                        key={`${attrData.uuid}_${i}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {value}
                    </a>
                );
            } else if (Array.isArray(value)) {
                textValue = _.compact(value).map((v, i) => {
                    return typeof v === "string" ? (
                        <div key={`link_${i}`}>
                            <a
                                href={v}
                                key={`${attrData.uuid}_${i}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {v}
                            </a>
                        </div>
                    ) : (
                        <div key={`link_${i}`}>
                            <a
                                href={v.text}
                                key={`${attrData.uuid}_${i}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {v.text}
                            </a>
                        </div>
                    );
                });
            } else if (value && value.hasOwnProperty("text")) {
                textValue =
                    (
                        <a
                            href={value.text}
                            key={`${attrData.uuid}_${i}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {value.text}
                        </a>
                    ) || "";
            } else {
                textValue = <div className="txt-error">Error: This value does not conform to standards</div>;
            }

            break;
        case "RichText":
            textValue = (
                <RichTextEditor
                    defaultValue={value}
                    readOnly={true}
                />
            );
            break;
        case "ColorPicklist":
            textValue = (
                <ColorPickerDisplay
                    type={type}
                    value={value}
                />
            );
            break;
        case "ImagePicklist":
            textValue = (
                <ImagePickerDisplay
                    type={type}
                    value={value}
                    provider={`/picklists/${attrData.additionalInfo.picklist.name}`}
                />
            );
            break;
        case "NutritionPanel":
            const isCodingTask = taskType?.includes("coding.standard");
            const isCodingQaTask = taskType === "codingqa.standard";

            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else if (isCodingQaTask || isCodingTask) {
                const standardRole = isCodingTask && taskType.split(".")[2].toUpperCase();
                roleType = isCodingTask ? coderRoleType[standardRole] : roleType;
                roleLabel = isCodingTask ? coderRoleLabel[standardRole] : roleLabel;

                textValue = (
                    <DraggablePanelButton
                        name="draggable"
                        text="View"
                        roleType={roleType}
                        roleLabel={roleLabel}
                    >
                        <NutritionReadOnly
                            value={value}
                            isCodingQA={taskType === "codingqa.standard"}
                        />
                    </DraggablePanelButton>
                );
            } else {
                textValue = (
                    <ModalButton
                        text="View"
                        name="standard"
                    >
                        <NutritionReadOnly value={value} />
                    </ModalButton>
                );
            }

            break;
        case "SupplementPanel":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = (
                    <ModalButton text="View">
                        <NutritionReadOnly
                            value={value}
                            showDescription={true}
                        />
                    </ModalButton>
                );
            }

            break;
        case "DrugPanel":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = (
                    <ModalButton text="View">
                        <ReadOnlyDrugPanel
                            value={value}
                            showDescription={true}
                        />
                    </ModalButton>
                );
            }

            break;
        case "EnergyPanel":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = (
                    <ModalButton text="View">
                        <ReadOnlyEnergyPanel value={value} />
                    </ModalButton>
                );
            }

            break;
        case "GuaranteedAnalysisPanel":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = (
                    <ModalButton text="View">
                        <ReadOnlyGuaranteedAnalysisPanel
                            value={value}
                            showDescription={true}
                        />
                    </ModalButton>
                );
            }

            break;
        case "LightingPanel":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = (
                    <ModalButton text="View">
                        <ReadOnlyLightingPanel value={value} />
                    </ModalButton>
                );
            }

            break;
        case "AttributeGroup":
            textValue = (
                <AttributeGroupFields
                    attribute={attrData}
                    defaultValue={value}
                    readOnly={true}
                    wipKey={wipKey}
                />
            );
            break;
        case "VarietyPack":
            textValue = (
                <SectionLaunch
                    launchLabel="View"
                    attribute={attrData}
                    defaultValue={value}
                    readOnly={true}
                    upc={upc}
                />
            );
            break;
        case "ShipmentLocationInfo":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else {
                textValue = value.location;
            }

            break;
        case "PegMeasurement":
            textValue = value ? displayMeasurements(value) : "";
            break;
        case "Client":
            if (!value || _.isEmpty(value)) {
                textValue = "";
            } else if (typeof value === "string") {
                textValue = value;
            } else if (value && value.hasOwnProperty("name")) {
                const isRetailer = value.isRetailer ? " (Retailer)" : "";
                textValue = `${value.name}${isRetailer}` || "";
            } else {
                textValue = <div className="txt-error">Error: This value does not conform to standards</div>;
            }

            break;
        default:
            if (typeof value === "object") {
                textValue = `[Can not display value of ${type}]`;
            }

            break;
    }

    return textValue;
};

export const isEmptyByType = (value, type = "") => {
    const useValue = _.isNumber(value) || value ? value : getDefaultValueByType(type);
    switch (type.toLowerCase()) {
        case "string":
        case "number":
        case "decimal":
            return _.isNumber(useValue) ? false : !useValue;
        case "boolean":
            return typeof value === "boolean" ? false : !useValue;
        case "stringlist":
        case "LinkList":
        case "numberlist":
        case "decimallist":
        case "measurementlist":
        case "colorpicklist":
        case "multidropdownpicklist":
        case "multidropdowncheckboxpicklist":
        case "pegmeasurement":
        case "featurebullet":
            return !useValue || _.isEmpty(useValue);
        case "drugpanel":
        case "drug_panel":
        case "energypanel":
        case "energy_panel":
        case "lightingpanel":
        case "lighting_panel":
        case "nutritionpanel":
        case "nutrition_panel":
        case "supplementpanel":
        case "supplement_panel":
        case "attributegroup":
        case "varietypack":
            return !useValue || _.isEmpty(useValue);
        default:
            return !useValue;
    }
};

export const getComponentTypes = () => {
    return get("/attribute-types").then((data) => {
        return _.chain(data).keyBy("typeId").mapValues("name").value();
    });
};

export const getAttributes = (codingWip, params = {}) => {
    const paramString = Object.keys(params).length > 0 ? `?${objectToParamString(params)}` : "";
    const url = `/coding/wips/${codingWip}${paramString}`;

    return get(url);
};

export const getSingleAttribute = (key, code) => {
    if (attributeCache[key] && attributeCache[key][code]) {
        return Promise.resolve(attributeCache[key][code]);
    }

    return getWip(key).then((wip) => {
        return wip.attributes[code] || getDefaultValueByType(code);
    });
};

export const sendAttributeData = ({ wipKey, shipmentKey, taskId, sourcePage }, payload) => {
    const paramString = objectToParamString({ shipmentKey, taskId, sourcePage });
    const urlParams = paramString ? `?${paramString}` : "";

    return patch(`/coding/wips/${wipKey}${urlParams}`, payload);
};

export const sendMultipleAttributesData = (codingWipKeys, shipmentKey, payload) => {
    codingWipKeys.forEach((wipKey) => {
        attributeCache[wipKey] = false; //clear attribute cache for key; it changed.
    });
    const attributesData = mapAttributeForSend(payload);
    const mapMultipleAttributes = codingWipKeys.map((wipKey) => {
        return {
            key: wipKey,
            patch: attributesData,
            shipmentKey: shipmentKey,
        };
    });

    return patch("/coding/wips/multiple", mapMultipleAttributes);
};

const mapAttributeForSend = (attr) => {
    const value = attr.value || isBoolean(attr.value) ? attr.value : getDefaultValueByType(attr.type);
    const obj = {};
    obj[attr.definition] = value;

    return obj;
};
export const sendSingleAttributeData = (wipKey, taskKey, sourcePage, payload) => {
    // Expects:
    // {
    //     definition: 'string',
    //     value: '',
    //     type: '',
    // }
    attributeCache[wipKey] = false; //clear attribute cache for key; it changed.
    const attribute = mapAttributeForSend(payload);

    return patch(`/coding/wips/${wipKey}?taskId=${taskKey}${setSourcePage(sourcePage, false)}`, attribute);
};

export const getAttributesByTerm = (term, wip, taskKey) => {
    // **** Expected To Return:
    // [
    //     {
    //         "key": "string",
    //         "name": "string",
    //         "shortDescription": "string",
    //         "dataType": "String",
    //         "tooltip": "string",
    //         "examples": "string",
    //         "limitTo": [
    //         {
    //             "text": "string",
    //             "value": "string"
    //         }
    //         ]
    //     }
    // ]
    const wipParam = wip ? `codingWipKey=${wip}&` : "";
    const taskKeyParam = taskKey ? `&taskKey=${taskKey}` : "";

    const url = `/coding/attributes?${wipParam}q=${term}${taskKeyParam}`;

    return get(url).then((attributes) => {
        if (attributes.success && !Array.isArray(attributes)) {
            return [];
        }

        return attributes.map((attr) => {
            return {
                ...attr,
                value: attr.uuid,
                text: attr.name,
                dataType: attr.componentType,
                defaultValue: (attr && attr.defaultValue) || "",
            };
        });
    });
};

export const getCoderQaAttributes = (codingWipKey) => {
    return get(`/coding/wips/${codingWipKey}/all`);
};

export const getAllCoderAttributeGroupsByTaskKey = (taskKey) => {
    return get(`/coding/attributes/groups?taskKey=${taskKey}`);
};

export const getBrandVerifiedQaGroupsByTaskKey = (taskKey) => {
    return get(`/coding/attributes/groups/wips?taskKey=${taskKey}`);
};

export const getAttributeValuesByTaskKey = (codingWipKey, taskKey, includeInvisible) => {
    const hiddenParam = includeInvisible ? "?includeInvisible=true" : "";

    return get(`/coding/wips/${codingWipKey}/tasks/${taskKey}${hiddenParam}`);
};

export const getAttributeDefinitionsByTaskKey = (codingWipKey, taskKey, subTaskKeys, referenceKeys) => {
    const paramString = referenceKeys.reduce((memo, key) => {
        return memo + `&pid=${key}`;
    }, "");
    const subTaskString = subTaskKeys.reduce((memo, key) => {
        return memo + `&subTaskKey=${key}`;
    }, "");
    const url = `/v2/coding/attributes/${codingWipKey}/${taskKey}?includeInvisible=true${subTaskString}${paramString}`;

    return get(url).then(mapAttributeData);
};

const extractAttributes = (attributeGroups) => {
    return attributeGroups.reduce((attributes, group) => {
        if (group.subGroups.length) {
            const nestedAttributes = extractAttributes(group.subGroups);

            attributes = [...attributes, ...nestedAttributes];
        }

        if (group.attributes && group.attributes.length) {
            attributes = [...attributes, ...group.attributes];
        }

        return attributes;
    }, []);
};

export const mapAttributeDataByTaskKey = (codingWipKey, taskKey, references, isBrandVerified, group) => {
    const bvScaffoldPromise =
        isBrandVerified &&
        getAttributesGroup(group, codingWipKey, taskKey).then((groups) => {
            return extractAttributes(groups.subGroups);
        });
    const defaultScaffoldPromise = !isBrandVerified && getAttributeDefinitionsByTaskKey(codingWipKey, taskKey, [], references);
    const valuesPromise = getAttributeValuesByTaskKey(codingWipKey, taskKey, true);
    const scaffoldPromise = isBrandVerified ? bvScaffoldPromise : defaultScaffoldPromise;

    return Promise.all([scaffoldPromise, valuesPromise]).then(([scaffold, attributeValues]) => {
        return {
            updatedOn: attributeValues["1880918225330826"],
            updatedBy: attributeValues["1880918225330825"],
            subGroups: scaffold.map((attr) => {
                return {
                    ...attr,
                    value: attributeValues[attr.uuid],
                };
            }),
        };
    });
};

export const getAttributesByGroup = (group, params = {}) => {
    params.groups = group;
    const url = `/coding/attributes?${objectToParamString(params)}`;

    return get(url).then(mapAttributeData);
};

export const getAttributeByUUID = (codingWipKey, uuid) => {
    return get(`/coding/wips/${codingWipKey}/attributes/${uuid}`).then((data) => {
        if (!data) {
            return null;
        }

        return mapAttribute(data);
    });
};

export const getAttributeDefinitionByUUID = (uuid) => {
    return get(`/coding/attributes/${uuid}`).then((data) => {
        if (!data) {
            return null;
        }

        return mapAttribute(data);
    });
};

export const deleteAttribute = (wipKey, taskKey, uuid, sourcePage) => {
    const source = sourcePage ? `&sourcePage=${sourcePage}` : "";

    return deleteRequest(`/coding/wips/${wipKey}/attributes/${uuid}?taskKey=${taskKey}${source}`);
};

export const attributesByGroup = (definitions) => {
    const groups = [];
    const byGroup = definitions.reduce((obj, a) => {
        const groupName = a.sortGroup ? a.sortGroup.name : "Miscellaneous";
        const sortGroup = a.sortGroup ? a.sortGroup : { name: "Miscellaneous", label: "Miscellaneous" };

        if (!obj[groupName]) {
            obj[groupName] = [];
            groups.push(sortGroup);
        }

        obj[groupName].push({ ...a });

        return obj;
    }, {});

    // Run Initial Sort
    Object.keys(byGroup).forEach((key) => {
        byGroup[key].sort(sortAttributes);
    });

    groups.sort((a, b) => {
        return a.sortOrder - b.sortOrder;
    });

    return {
        groups,
        byGroup,
    };
};

export const isEqual = (...objects) => objects.every((obj) => JSON.stringify(obj) === JSON.stringify(objects[0]));

export const checkUpdate = (oldValue, value, type) => {
    switch (type) {
        case "AttributeGroup":
        case "Measurement":
        case "Picklist":
        case "CascadingPicklist":
        case "DropdownPickList":
        case "DrugPanel":
        case "EnergyPanel":
        case "LightingPanel":
        case "NutritionPanel":
        case "GuaranteedAnalysisPanel":
        case "SupplementPanel":
        case "VarietyPack":
        case "MultiDropdownCheckboxPicklist":
        case "ImagePicklist":
            //TODO: This will fail on objects where numbers change to strings but is better than always updating
            return !isEqual(oldValue, value);
        default:
            const existingValue = oldValue || typeof oldValue === "boolean" ? oldValue.toString() : "";
            const newValue = value || typeof value === "boolean" ? value.toString() : "";

            return existingValue !== newValue;
    }
};

export const handleTriggeredChanges = (triggeredChanges, values) => {
    const added = triggeredChanges && triggeredChanges.added ? triggeredChanges.added : {};
    const updated = triggeredChanges && triggeredChanges.updated ? triggeredChanges.updated : {};
    values = {
        ...values,
        ...updated,
        ...added,
    };

    if (triggeredChanges && triggeredChanges.removed) {
        triggeredChanges.removed.forEach((uuid) => {
            delete values[uuid];
        });
    }

    return values;
};

export const bulkDownloadAttributes = (searchValue) => {
    return get(`/attributes/bulk-download?contains=${searchValue}`);
};

export const getProfilingProductTaskInfo = (params) => {
    const payload = {
        taskContextKeys: params,
    };

    const url = `/tasking/tasks/scheduling.default/search`;

    return post(url, payload);
};

export const getAttributesGroup = (groupName, wipKey, taskKey) => {
    return get(`/coding/attributes/group?groupName=${groupName}&codingWipKey=${wipKey}${taskKey ? `&taskKey=${taskKey}` : ""}`).then(
        mapSubgroupsAttributes
    );
};

export const getAttributesGroupByTaskKey = (groupName, taskKey) => {
    return get(`/coding/attributes/group?groupName=${groupName}&taskKey=${taskKey}`).then(mapSubgroupsAttributes);
};

export const validateNutrients = ({ attribute }) => {
    return post(`/data-quality-validation/panels`, attribute);
};

export const approveRejectAttribute = (payload) => {
    return patch(`/attributes/approve-reject`, payload);
};
